import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FileUploader from "@/components/FileUploader";
import { Plus, Pencil, Trash2, Users } from "lucide-react";

const EMPTY = { name: "", role: "", photo: "", order: 1, published: true };

export default function AdminTeam() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/team").then((r) => setItems((r.data || []).sort((a, b) => (a.order || 0) - (b.order || 0)))).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    const payload = { ...form, order: Number(form.order) || 1 };
    if (editing) await api.put(`/admin/team/${editing.id}`, payload);
    else await api.post("/admin/team", payload);
    toast.success("Saved"); setOpen(false); load();
  };
  const del = async (t) => { if (!window.confirm(`Delete "${t.name}"?`)) return; await api.delete(`/admin/team/${t.id}`); toast.success("Deleted"); load(); };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-3xl text-navy">Our Team</h1><p className="text-muted-foreground mt-1">Manage team members shown on the About page.</p></div>
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="add-team-btn" className="bg-gold text-navy hover:bg-gold-dark font-semibold"><Plus className="w-4 h-4 mr-1" /> Add Member</Button>
      </div>
      {items === null ? <Skeleton className="h-64 rounded-2xl" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <div key={t.id} className="bg-white rounded-2xl border border-gray-100 shadow-card p-5 flex items-center gap-4" data-testid={`team-admin-${t.id}`}>
              {t.photo ? <img src={t.photo} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 rounded-full bg-navy/10 flex items-center justify-center"><Users className="w-7 h-7 text-navy/40" /></div>}
              <div className="flex-1"><p className="font-semibold text-navy">{t.name}</p><p className="text-xs text-gold">{t.role}</p><span className={`text-xs mt-1 inline-block px-2 py-0.5 rounded-full ${t.published ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{t.published ? "Shown" : "Hidden"}</span></div>
              <div className="flex flex-col gap-1">
                <Button size="icon" variant="ghost" onClick={() => { setEditing(t); setForm({ ...EMPTY, ...t }); setOpen(true); }} data-testid={`edit-team-${t.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(t)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-center text-muted-foreground py-10 sm:col-span-2 lg:col-span-3">No team members yet.</p>}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="team-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">{editing ? "Edit" : "Add"} Team Member</DialogTitle></DialogHeader>
          <div><Label className="text-xs font-semibold text-navy">Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-gray-50 mt-1" data-testid="tmf-name" /></div>
          <div><Label className="text-xs font-semibold text-navy">Role / Designation</Label><Input value={form.role} onChange={(e) => set("role", e.target.value)} className="bg-gray-50 mt-1" data-testid="tmf-role" /></div>
          <div><Label className="text-xs font-semibold text-navy">Display Order</Label><Input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className="bg-gray-50 mt-1" /></div>
          <FileUploader mode="image" value={form.photo} onChange={(v) => set("photo", v)} label="Photo" />
          <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => set("published", v)} /><Label className="text-sm">Show on website</Label></div>
          <Button onClick={save} data-testid="save-team-btn" className="bg-gold text-navy hover:bg-gold-dark font-semibold">Save Member</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
